import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteKioskPhotoQuery, getKioskPhotosQuery, uploadKioskPhotoQuery } from '../services/taskService';
import { apiErrorMessage } from '../utilities/apiError';
import { queryKeys } from './queryKeys';

// The photos uploaded for a board's kiosk display (the ones typed in as links live on the board itself, in data/board.ts).

export const kioskPhotosOptions = (boardId: string) => ({
    queryKey: queryKeys.kioskPhotos(boardId),
    queryFn: () => getKioskPhotosQuery(boardId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
});

// `enabled: false` waits (a viewer who can't manage the board has no use for the list). `refetchInterval` re-reads on a
// timer: the photo links are presigned and expire after an hour, so a display that stays open keeps asking for fresh ones.
export function useKioskPhotos(boardId: string, options: { enabled?: boolean, refetchInterval?: number } = {}) {
    const { enabled, refetchInterval } = options;
    return useQuery({
        ...kioskPhotosOptions(boardId),
        ...(enabled === undefined ? {} : { enabled }),
        ...(refetchInterval ? { refetchInterval, refetchIntervalInBackground: true } : {}),
    });
}

// The list is read back after a change rather than patched: the list endpoint is what mints the presigned URLs the
// thumbnails are drawn from.
const refreshKioskPhotos = (queryClient: QueryClient, boardId: string) =>
    queryClient.invalidateQueries({ queryKey: queryKeys.kioskPhotos(boardId) });

export const uploadKioskPhotoMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: (file: File) => uploadKioskPhotoQuery(boardId, file),
    onSuccess: () => refreshKioskPhotos(queryClient, boardId),
});

export const deleteKioskPhotoMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: (photoId: string) => deleteKioskPhotoQuery(photoId),
    onSuccess: () => refreshKioskPhotos(queryClient, boardId),
});

// What to tell the person when an upload fails: the server's reason if it gave one.
export const uploadErrorMessage = (error: any): string => apiErrorMessage(error, 'Failed to upload photo');

export const useUploadKioskPhoto = (boardId: string) => useMutation(uploadKioskPhotoMutation(useQueryClient(), boardId));
export const useDeleteKioskPhoto = (boardId: string) => useMutation(deleteKioskPhotoMutation(useQueryClient(), boardId));
